import React, { memo, useContext, useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import * as THREE from "three";
import { OutlinePass } from "three/examples/jsm/postprocessing/OutlinePass";
import { COLORS } from "../../constants";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";
import { FXAAShader } from "three/examples/jsm/shaders/FXAAShader";
import { SceneContext } from "../Context/SceneContext";
import { useWindowProperty } from "../../utils/hooks/useWidnowProperty";
import { useCurrent } from "../../hooks/useCurrent";
import {MainScene} from "../MainCanvas/MainScene";

export const CustomOutline: React.FunctionComponent = () => {
    const { scene, gl, size, camera } = useThree();
    const composerRef = useRef<EffectComposer>();
    const outlineRef = useRef<OutlinePass>();
    const renderPassRef = useRef<RenderPass>();
    const fxaaPassRef = useRef<ShaderPass>();
    const activeObject = useContext(SceneContext);
    const shadersDebug = useWindowProperty("shaders", true);
    const { currentEditItem } = useCurrent();

    useEffect(() => {
        // Используем существующий рендерер из R3F вместо создания нового
        const renderer = gl;
        const sizes = size;
        const _pixelRatio = renderer.getPixelRatio();

        const _width = sizes.width;
        const _height = sizes.height;
        const renderTarget = new THREE.WebGLRenderTarget(_width * _pixelRatio, _height * _pixelRatio, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            type: THREE.FloatType,
            format: THREE.RGBFormat,
            encoding: THREE.LinearEncoding,
        });
        renderTarget.texture.name = "EffectComposer.rt1";

        const COMPOSER = new EffectComposer(renderer, renderTarget);
        composerRef.current = COMPOSER;

        const renderPass = new RenderPass(scene, camera);
        renderPass.setSize(sizes.width, sizes.height);
        renderPassRef.current = renderPass;
        COMPOSER.addPass(renderPass);

        const outlinePass = new OutlinePass(new THREE.Vector2(sizes.width, sizes.height), scene, camera);
        outlineRef.current = outlinePass;

        outlinePass.visibleEdgeColor.set(COLORS.outline);
        outlinePass.hiddenEdgeColor.set(COLORS.outline);
        outlinePass.edgeStrength = 3;

        COMPOSER.addPass(outlinePass);

        const fxaaPass = new ShaderPass(FXAAShader);
        fxaaPass.material.uniforms["resolution"].value.x = 1 / (sizes.width * _pixelRatio);
        fxaaPass.material.uniforms["resolution"].value.y = 1 / (sizes.height * _pixelRatio);
        fxaaPass.renderToScreen = true;
        fxaaPassRef.current = fxaaPass;

        COMPOSER.addPass(fxaaPass);
    }, [scene, camera, gl, size]);

    useEffect(() => {
        const renderer = composerRef.current?.renderer;
        if (renderer) {
            const sizes = size;
            // Используем правильный pixelRatio вместо фиксированного 1.25
            const pixelRatio = Math.min(window.devicePixelRatio, 2);
            renderer.setPixelRatio(pixelRatio);
            const _pixelRatio = renderer.getPixelRatio();
            renderer.setSize(sizes.width, sizes.height);
            renderPassRef.current?.setSize(sizes.width, sizes.height);
            if (fxaaPassRef.current) {
                fxaaPassRef.current.material.uniforms["resolution"].value.x = 1 / (size.width * _pixelRatio);
                fxaaPassRef.current.material.uniforms["resolution"].value.y = 1 / (size.height * _pixelRatio);
            }
        }
    }, [size]);

    //Пока не трогаем Melnikov
    // useEffect(() => {
    //     if (!composerRef.current) {
    //         return
    //     }
    //     composerRef.current?.passes.map(pass => {
    //         if (pass instanceof OutlinePass || pass instanceof ShaderPass) {
    //             pass.enabled = shadersDebug
    //         }
    //     })
    // }, [shadersDebug]);

    useEffect(() => {
        if (!currentEditItem.id && activeObject) {
            activeObject.current = null;
        }

        if (outlineRef.current && activeObject?.current) {
            outlineRef.current.selectedObjects = [activeObject.current];
        }
        if (outlineRef.current && activeObject?.current === null) {
            outlineRef.current.selectedObjects = [];
        }
    }, [activeObject?.current, currentEditItem]);

    return useFrame(() => {
        if (shadersDebug) {
            composerRef.current && composerRef.current.render();
        } else {
            gl.render(scene, camera);
        }
    }, 1);
};
